import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PortalService } from '../../../../../services/portal.service';
import { EnrollmentFormService } from '../../../../../services/EnrollmentFormService';
import { Router } from '@angular/router';
import { AddressValidationComponent } from '../../modals/address-validation/address-validation.component';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ProfileComponent implements OnInit {
  profileForm!: FormGroup;
  patientAccountId: any;
  states: any;
  allFieldsFilled: boolean = false;
  isLoading = true;
  EmailID: string;
  isSubmitted: true;
  homeaAddressAccepted: boolean;
  shippingAddressAccepted: boolean;
  phoneTypes: { [key: number]: string } = {
    1: 'Home',
    2: 'Work'
  };

  constructor(private fb: FormBuilder, private portalService: PortalService, private efs: EnrollmentFormService, private router: Router, private dialog: MatDialog) { }

  ngOnInit(): void {
    this.homeaAddressAccepted = false
    this.shippingAddressAccepted = false;
    this.EmailID = localStorage.getItem('email');
    this.loadProfileData();
    this.portalService.getStatesList().subscribe({
      next: (data) => {
        this.states = data;
      },
      error: (err) => {
        console.error('Error loading states:', err);
      },
    });
    this.patientAccountId = this.getPatientAccountId('patientAccountId');
  }

  getPhoneTypeKeys(): number[] { return Object.keys(this.phoneTypes).map(key => +key); }

  getPatientAccountId(value: any): number | null {
    const storedId = localStorage.getItem(value);
    return storedId ? parseInt(storedId, 10) : null;
  }

  isSameAsHomeAddress(data: any) {
    let result = false;
    if (data.isSubmitted && (data.billingAddress1 === data.shippingAddress1 || data.billingAddress2 === data.shippingAddress2 ||
      data.billingCity === data.shippingCity || data.billingState === data.shippingState || data.billingZip === data.shippingZip)
    ) {
      result = true;
    }
    return result;
  }

  // Method to load profile data from the backend (through PortalService)
  loadProfileData(): void {
    this.portalService.getPatientAccountDetail(this.EmailID).subscribe(
      (data: any) => {
        this.isLoading = false;
        this.profileForm = this.fb.group({
          firstName: [data.firstName, Validators.required],
          lastName: [data.lastName, Validators.required],
          birthdate: [data.birthdate, Validators.required],
          email: [data.email, [Validators.required, Validators.email]],
          primaryPhoneTypeId: [data.primaryPhoneTypeId, Validators.required],
          primaryPhone: [data.phoneNumber, Validators.required],
          alternatePhoneTypeId: [data.alternatePhoneTypeId],
          alternatePhone: [data.altPhone],
          addressLine1: [data.billingAddress1, Validators.required],
          addressLine2: [data.billingAddress2],
          city: [data.billingCity, Validators.required],
          state: [data.billingState, Validators.required],
          zipCode: [data.billingZip, [Validators.required, Validators.maxLength(5)]],
          shippingAddressLine1: [data.shippingAddress1, Validators.required],
          shippingAddressLine2: [data.shippingAddress2],
          shippingCity: [data.shippingCity, Validators.required],
          shippingState: [data.shippingState, Validators.required],
          shippingZipCode: [data.shippingZip, [Validators.required, Validators.maxLength(5)]],
          sameAsHomeAddress: [this.isSameAsHomeAddress(data)],
        });
        //this.allFieldsFilled = this.checkAllFieldsFilled();

        // Subscribe to sameAsHomeAddress toggle changes
        this.profileForm.get('sameAsHomeAddress')?.valueChanges.subscribe((value) => {
          if (!data.isSubmitted) this.onToggleChange(value);
        });

        if (data.isSubmitted) {
          this.profileForm.disable();
        }
        localStorage.setItem('patientAccountId', data.patientAccountId);
        this.patientAccountId = data.patientAccountId;
      },
      (error: any) => {
        console.error('Error loading profile data:', error);
      }
    );
  }

  // Method to check if all fields are filled
  checkAllFieldsFilled(): boolean {
    return Object.keys(this.profileForm.controls).every((key) => {
      const control = this.profileForm.get(key);
      return control?.value !== null && control?.value !== undefined && control?.value !== '';
    });
  }

  // Apply readonly logic based on form field conditions
  applyReadonlyLogic(): void {
    Object.keys(this.profileForm.controls).forEach((key, index) => {
      const control = this.profileForm.get(key);
      if (this.allFieldsFilled) {
        control?.disable(); // Disable all fields if all fields are filled
      } else if (index <= 6) {
        control?.disable(); // Disable fields up to alternatePhone
      }
    });
  }

  updateProfile(profileData: any) {
    this.isLoading = true;
    this.portalService.updatePatientProfile(profileData, this.patientAccountId).subscribe(
      (response) => {
        this.isLoading = false;
        console.log('Profile saved successfully:', response);
        this.profileForm.disable();
        this.router.navigate(['/submissions']);
      },
      (error) => {
        this.isLoading = false;
        console.error('Error saving profile data:', error);
      }
    );
  }

  // Method to handle form submission
  onSubmit(): void {
    console.log('Form Valid:', this.profileForm.valid);
    console.log('Form Values:', this.profileForm.value);
    this.isLoading = true;

    if (this.profileForm.valid) {
      const profileData = {
        firstName: this.profileForm.value.firstName,
        lastName: this.profileForm.value.lastName,
        birthdate: this.profileForm.value.birthdate,
        email: this.profileForm.value.email,
        primaryPhoneTypeId: this.profileForm.value.primaryPhoneTypeId,
        phoneNumber: this.profileForm.value.primaryPhone,
        alternatePhoneTypeId: this.profileForm.value.alternatePhoneTypeId,
        altPhone: this.profileForm.value.alternatePhone,
        billingAddress1: this.profileForm.value.addressLine1,
        billingAddress2: this.profileForm.value.addressLine2,
        billingCity: this.profileForm.value.city,
        billingState: this.profileForm.value.state,
        billingZip: this.profileForm.value.zipCode,
        shippingAddress1: this.profileForm.value.shippingAddressLine1,
        shippingAddress2: this.profileForm.value.shippingAddressLine2,
        shippingCity: this.profileForm.value.shippingCity,
        shippingState: this.profileForm.value.shippingState,
        shippingZip: this.profileForm.value.shippingZipCode,
        isSubmitted: true,
        isActive: true,
        createdDate: new Date().toISOString(),
        updatedDate: new Date().toISOString(),
      };
      //this.updateProfile(profileData);

      const currentHomeAddress: any = {
        'address1': this.profileForm.value.addressLine1,
        'address2': this.profileForm.value.addressLine2,
        'city': this.profileForm.value.city,
        'state': this.profileForm.value.state,
        'zip': this.profileForm.value.zipCode,
      }
      const currentShippingAddress: any = {
        'address1': this.profileForm.value.shippingAddressLine1,
        'address2': this.profileForm.value.shippingAddressLine2,
        'city': this.profileForm.value.shippingCity,
        'state': this.profileForm.value.shippingState,
        'zip': this.profileForm.value.shippingZipCode
      }

      const areAddressesSame =
        JSON.stringify(currentHomeAddress) === JSON.stringify(currentShippingAddress);

      if (areAddressesSame) {
        this.portalService.validateAddress(currentHomeAddress).subscribe((response) => {
          this.isLoading = false;
          let homeAddressAccept = false;

          if (!response.data.status && !homeAddressAccept) {
            const dialogRef = this.dialog.open(AddressValidationComponent, {
              data: currentHomeAddress,
              maxWidth: '30vw',
              autoFocus: false,
              disableClose: true,
            });

            dialogRef.afterClosed().subscribe((isConfirm) => {
              if (isConfirm) {
                homeAddressAccept = true;
                this.updateProfile(profileData);
              }
            });
          } else {
            this.updateProfile(profileData);
          }
        });
      }
      else {
        forkJoin([this.portalService.validateAddress(currentHomeAddress),
        this.portalService.validateAddress(currentShippingAddress)]).subscribe(responses => {
          let shippingAddressAccept = false;
          let homeAddressAccept = false;
          if (!responses[0].data.status && !this.homeaAddressAccepted) {
            this.isLoading = false;
            const dialogRef = this.dialog.open(AddressValidationComponent, {
              //data: responses[0],
              data: currentHomeAddress,
              maxWidth: '30vw',
              autoFocus: false,
              disableClose: true,
            });
            dialogRef.afterClosed().subscribe(isConfirm => {
              if (isConfirm) {
                homeAddressAccept = true;
                this.homeaAddressAccepted = true;
                if (shippingAddressAccept && homeAddressAccept) {
                  this.updateProfile(profileData);
                } else {
                  return;
                }
              }
            })
          } else {
            homeAddressAccept = true;
            this.isLoading = false;
          }

          if (!responses[1].data.status && !this.shippingAddressAccepted) {
            this.isLoading = false;
            const dialogRef = this.dialog.open(AddressValidationComponent, {
              //data: responses[1],
              data: currentShippingAddress,
              maxWidth: '30vw',
              autoFocus: false,
              disableClose: true,
            });
            dialogRef.afterClosed().subscribe(isConfirm => {
              if (isConfirm) {
                shippingAddressAccept = true;
                this.shippingAddressAccepted = true;
                if (shippingAddressAccept && homeAddressAccept) {
                  this.updateProfile(profileData);
                } else {
                  return;
                }
              }
            })
          } else {
            shippingAddressAccept = true;
            this.isLoading = false;
          }
          if (shippingAddressAccept && homeAddressAccept) {
            this.updateProfile(profileData);
          }
        });
      }
      console.log('Payload Sent to API:', profileData);
      console.log('Patient Account ID:', this.patientAccountId);


    } else {
      this.isLoading = false;
      console.error('Form is invalid. Please correct the errors.');
    }
  }


  clear(): void {
    this.profileForm.patchValue({
      alternatePhoneTypeId: '',
      alternatePhone: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zipCode: '',
      shippingAddressLine1: '',
      shippingAddressLine2: '',
      shippingCity: '',
      shippingState: '',
      shippingZipCode: '',
      sameAsHomeAddress: false,
    });
    this.profileForm.markAsUntouched();
    this.profileForm.markAsPristine();
    // Enable all controls to make the form editable
    // Object.keys(this.profileForm.controls).forEach((key) => {
    //   this.profileForm.get(key)?.enable();
    // });

    // Reset `allFieldsFilled` to false
    this.allFieldsFilled = false;
  }

  onToggleChange(value: boolean): void {
    if (value) {
      this.profileForm.patchValue({
        shippingAddressLine1: this.profileForm.get('addressLine1')?.value,
        shippingAddressLine2: this.profileForm.get('addressLine2')?.value,
        shippingCity: this.profileForm.get('city')?.value,
        shippingState: this.profileForm.get('state')?.value,
        shippingZipCode: this.profileForm.get('zipCode')?.value,
      });
    } else {
      this.profileForm.patchValue({
        shippingAddressLine1: '',
        shippingAddressLine2: '',
        shippingCity: '',
        shippingState: '',
        shippingZipCode: '',
      });
    }
  }
}
